/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { meetingsAPI, viewsAPI } from '../lib/api';
import { formatDate, formatDateTime, formatRelativeTime, MEETING_TYPES, TARGET_AUDIENCES, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, Clock, MapPin, Users, Loader2, FileText, Trash2,
  ListOrdered, Info, Lock, Printer, User, CalendarDays,
} from 'lucide-react';

const MeetingDetailPage = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const { user } = useAuth();
  usePageTitle('Toplanti Detay');
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = ['admin', 'genel_sekreter', 'meclis_baskani'].includes(user?.role);

  useEffect(() => { loadMeeting(); }, [meetingId]);

  useEffect(() => {
    if (meetingId) viewsAPI.track('meeting', meetingId).catch(() => {});
  }, [meetingId]);

  const loadMeeting = async () => {
    try {
      const response = await meetingsAPI.getById(meetingId);
      setMeeting(response.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error('Bu toplantiyi goruntuleme yetkiniz yok');
      else toast.error('Toplanti yuklenirken hata olustu');
      navigate('/panel/toplantilar');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!confirm('Bu toplantiyi silmek istediginizden emin misiniz?')) return;
    try {
      await meetingsAPI.delete(meetingId);
      toast.success('Toplanti silindi');
      navigate('/panel/toplantilar');
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Toplanti silinemedi'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!meeting) return null;

  const typeConfig = {
    genel_kurul: { gradient: 'from-emerald-800 via-emerald-900 to-emerald-800', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    meclis_toplantisi: { gradient: 'from-blue-800 via-blue-900 to-blue-800', badge: 'bg-blue-100 text-blue-800 border-blue-300' },
    calisma_grubu: { gradient: 'from-purple-800 via-purple-900 to-purple-800', badge: 'bg-purple-100 text-purple-800 border-purple-300' },
  };
  const tCfg = typeConfig[meeting.meeting_type] || { gradient: 'from-slate-800 via-slate-900 to-slate-800', badge: 'bg-slate-100 text-slate-800 border-slate-300' };
  const typeInfo = MEETING_TYPES[meeting.meeting_type] || { label: meeting.meeting_type };
  const isPast = new Date(meeting.date) < new Date();

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="max-w-5xl mx-auto space-y-6 print-area" data-testid="meeting-detail-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between no-print">
          <Button variant="ghost" size="sm" onClick={() => navigate('/panel/toplantilar')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Toplantilara Don
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" /> Yazdir
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete} data-testid="delete-meeting-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            )}
          </div>
        </div>

        {/* Hero Header */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className={`bg-gradient-to-r ${tCfg.gradient} px-6 py-5`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={`${tCfg.badge} border px-3 py-0.5 text-xs font-semibold`}>{typeInfo.label}</Badge>
                  <Badge className={`border px-3 py-0.5 text-xs font-semibold ${isPast ? 'bg-slate-100 text-slate-600 border-slate-300' : 'bg-emerald-100 text-emerald-700 border-emerald-300'}`}>
                    {meeting.status === 'planlanmis' ? 'Planlanmis' : 'Tamamlandi'}
                  </Badge>
                </div>
                <h1 className="text-xl font-bold text-white">{meeting.title}</h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-white/60">
                  <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {formatDate(meeting.date)} - {meeting.time}</span>
                  {meeting.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {meeting.location}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Date countdown strip */}
          {!isPast && (
            <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-3 flex items-center gap-3">
              <Clock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-emerald-900">Yaklasan toplanti - {formatDate(meeting.date)}, {meeting.time}</span>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Description */}
            {meeting.description && (
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Info className="w-4 h-4 text-slate-500" /> Aciklama
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{meeting.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Agenda */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-slate-500" /> Gundem Maddeleri
                </CardTitle>
              </CardHeader>
              <CardContent>
                {meeting.agenda && meeting.agenda.length > 0 ? (
                  <div className="space-y-2">
                    {meeting.agenda.map((item, idx) => (
                      <div key={`agenda-${idx}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                        <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-slate-700 pt-1">{item}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                      <ListOrdered className="w-5 h-5 text-slate-300" />
                    </div>
                    <p className="text-sm text-slate-400">Gundem maddesi belirtilmemis</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Meeting Info Card */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Toplanti Bilgileri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Tarih</p>
                    <p className="text-sm text-slate-900 font-semibold">{formatDate(meeting.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Saat</p>
                    <p className="text-sm text-slate-900 font-semibold">{meeting.time}</p>
                  </div>
                </div>
                {meeting.location && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Konum</p>
                      <p className="text-sm text-slate-900 font-semibold">{meeting.location}</p>
                    </div>
                  </div>
                )}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Olusturulma</p>
                      <p className="text-sm text-slate-600">{formatDateTime(meeting.created_at)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Target Audience */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" /> Hedef Kitle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {meeting.target_audience?.map((a) => (
                    <Badge key={a} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs px-3 py-1">
                      {TARGET_AUDIENCES.find((t) => t.value === a)?.label || a}
                    </Badge>
                  ))}
                </div>
                {meeting.target_group_ids?.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-800">Belirli gruplara kisitli erisim</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Files */}
            {meeting.files && meeting.files.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" /> Dosyalar
                    <Badge variant="secondary" className="text-xs ml-1">{meeting.files.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {meeting.files.map((file, idx) => (
                      <div key={`participant-${idx}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                        <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{file.name || `Dosya ${idx + 1}`}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MeetingDetailPage;
